import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import type { ExamTakingQuestion } from '@proref/types';
import { Icon } from '../components/shared/Icons';
import { Button } from '../components/shared/Button';
import { Spinner } from '../components/shared/Spinner';
import { Skeleton } from '../components/shared/Skeleton';
import { VideoPlayer } from '../components/shared/VideoPlayer';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { apiErrorMessage } from '../lib/api';
import { notifyError } from '../lib/toast';
import { useExamAttemptQuery, useSaveExamAnswer, useSubmitExamAttempt } from '../hooks/use-exams';
import { useExamProctoring, clearProctoringStorage } from '../hooks/use-exam-proctoring';

function mmss(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Option letters — a/b/c/… beside each choice, matching how the admin editor and the printed
// rule books label answers, so a candidate can discuss "answer b" out loud.
const OPTION_LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

function QuestionBody({
  question,
  selected,
  onToggle,
}: {
  question: ExamTakingQuestion;
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      {question.clip ? (
        <div className="mb-4">
          {question.clip.playback_url ? (
            <VideoPlayer src={question.clip.playback_url} poster={question.clip.poster_url ?? undefined} className="w-full" />
          ) : (
            <div className="grid h-48 place-items-center rounded border border-dashed border-line-2 bg-bg-2 text-sm text-muted">
              {t('exams.runner.clipUnavailable')}
            </div>
          )}
          <p className="mt-3 text-[15px] font-medium text-ink">{t('exams.runner.whatsCorrectCall')}</p>
        </div>
      ) : (
        <>
          {question.rule_article && (
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-line-2 bg-surface-2 px-2.5 py-0.5 font-mono text-[10.5px] text-ink-2">
              <Icon.doc className="h-3 w-3" />{' '}
              {t('exams.runner.ruleWithTitle', { number: question.rule_article.number, title: question.rule_article.title })}
            </div>
          )}
          <p className="mb-1 text-[15px] font-medium text-ink">{question.question_text || t('exams.runner.whatsCorrectCall')}</p>
        </>
      )}

      {/* Deliberately does NOT say how many answers are correct. Knowing the count tells you when
          to stop looking, which is a large part of the difficulty on a rule test. */}
      <p className="mb-3 text-[12px] italic text-muted">{t('exams.runner.selectAllCorrect')}</p>

      <div className="flex flex-col gap-2">
        {question.options.map((opt, i) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              // aria-pressed rather than a real checkbox: the whole row is the hit target, and a
              // toggle button carries the same "on/off" semantics to a screen reader.
              aria-pressed={active}
              onClick={() => onToggle(opt)}
              className={
                'flex w-full items-start gap-3 rounded-btn border-[1.5px] px-4 py-3 text-left text-[14px] transition-colors ' +
                (active ? 'border-accent bg-accent/10 text-ink' : 'border-line bg-surface text-ink-2 hover:border-line-3')
              }
            >
              <span
                className={
                  'mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-[4px] border-[1.5px] ' +
                  (active ? 'border-accent bg-accent text-white' : 'border-line-3 bg-surface')
                }
                aria-hidden="true"
              >
                {active && <Icon.check className="h-2.5 w-2.5" />}
              </span>
              <span className="font-semibold text-ink-3">{OPTION_LETTERS[i]}）</span>
              <span className="flex-1">{opt}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// The test itself, driven by an attempt id and a single `onSubmitted` callback (fired on manual
// submit, auto-submit at time-up, or opening an already-finished attempt). Rendered both as a
// full page (route) and inside a focused Dialog — it owns no layout chrome of its own so either
// host can frame it.
export function ExamRunner({
  attemptId,
  onSubmitted,
  // The dialog host's ✕ is absolutely positioned over this component's top-right corner, where the
  // countdown sits — so the host, which is the only thing that knows the ✕ is there, asks for the
  // gutter. The full-page host has no ✕ and keeps the countdown flush with the content edge.
  reserveTopRight = false,
}: {
  attemptId: string | undefined;
  onSubmitted: () => void;
  reserveTopRight?: boolean;
}) {
  const { t } = useTranslation();
  const id = attemptId;
  const { data: state, isLoading, isError, error } = useExamAttemptQuery(id);
  const save = useSaveExamAnswer(id ?? '');
  const submit = useSubmitExamAttempt();

  // Monitoring runs only on an Official (proctored) exam, and only while it's unsubmitted — a
  // practice test counts nothing and blocks nothing.
  const proctored = Boolean(state?.exam.is_proctored) && state?.attempt.completed_at === null;
  const proctoring = useExamProctoring(id, proctored);

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [remaining, setRemaining] = useState<number | null>(null);
  // Guards so an expired/auto-submit (and the finished-attempt hand-off) fire exactly once.
  const submittingRef = useRef(false);
  const finishedRef = useRef(false);

  // Seed the local answer map from whatever was saved before (resume).
  useEffect(() => {
    if (!state) return;
    const seed: Record<string, string[]> = {};
    for (const a of state.answers) {
      if (a.selected_answers.length > 0) seed[a.exam_question_id] = a.selected_answers;
    }
    setAnswers(seed);
  }, [state]);

  // An already-submitted attempt → hand straight off to results (e.g. a stale link / reopened).
  useEffect(() => {
    if (state && state.attempt.completed_at !== null && !finishedRef.current) {
      finishedRef.current = true;
      onSubmitted();
    }
  }, [state, onSubmitted]);

  async function doSubmit() {
    if (!id || submittingRef.current) return;
    submittingRef.current = true;
    try {
      await submit.mutateAsync(id);
      // The server owns the tallies from here; a leftover local total would otherwise ride
      // along into a retake of the same exam.
      clearProctoringStorage(id);
      onSubmitted();
    } catch (err) {
      submittingRef.current = false;
      notifyError(err, t('exams.runner.couldNotSubmit'));
    }
  }

  // Countdown — tick every second off the deadline; auto-submit at zero.
  const deadline = state?.exam.deadline ?? null;
  useEffect(() => {
    if (!deadline) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const left = (new Date(deadline).getTime() - Date.now()) / 1000;
      setRemaining(left);
      if (left <= 0) void doSubmit();
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // doSubmit is stable enough for this timer; deadline is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  const questions = useMemo(() => state?.questions ?? [], [state]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (isError || !state) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('exams.runner.couldNotLoadAttempt'))}
      </div>
    );
  }

  const question = questions[current];
  // A question counts as answered only while it holds at least one tick — un-ticking the last
  // option puts it back in the "still to do" pile rather than leaving it falsely done.
  const answeredCount = Object.values(answers).filter((a) => a.length > 0).length;

  function toggle(value: string) {
    if (!question) return;
    const before = answers[question.id] ?? [];
    // Every option toggles independently — ALWAYS, including on a question that happens to have a
    // single correct answer. Making those behave like radio buttons was wrong twice over: the
    // candidate couldn't tick a second option on a question that wanted two, and the control type
    // itself gave away how many answers were expected before they had answered.
    const next = before.includes(value)
      ? before.filter((v) => v !== value)
      : [...before, value];

    setAnswers((prev) => ({ ...prev, [question.id]: next }));
    // Autosave; a 409 means the deadline passed → finalise.
    save.mutate(
      { exam_question_id: question.id, selected_answers: next },
      {
        onError: (err) => {
          notifyError(err, t('exams.runner.couldNotSaveAnswer'));
          void doSubmit();
        },
      },
    );
  }

  return (
    <div className="relative">
      {/* Proctoring gate. Blurs the exam behind it — best-effort against a screen capture of a
          backgrounded window — and stays up until the candidate explicitly acknowledges, so it
          can't be dismissed by clicking straight through on return. */}
      {(proctoring.warning || proctoring.needsFullscreen) && (
        <div className="absolute inset-0 z-50 grid place-items-center rounded-lg bg-ink/95 px-6 backdrop-blur-sm">
          <div className="max-w-md text-center text-white">
            <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-white/15 ring-2 ring-white/40">
              <Icon.lock className="h-7 w-7" />
            </div>
            {/* Two reasons to be here, and the same button clears both — it re-enters fullscreen
                from inside the click handler, which is the only moment the browser will grant it. */}
            <h2 className="mb-2 text-[20px] font-semibold">
              {proctoring.warning ? t('exams.runner.leftExamTitle') : t('exams.runner.fullscreenTitle')}
            </h2>
            <p className="mb-5 text-[13.5px] leading-relaxed text-white/90">
              {proctoring.warning ? t('exams.runner.leftExamBody') : t('exams.runner.fullscreenBody')}
            </p>
            <Button variant="default" onClick={proctoring.acknowledge}>
              {proctoring.warning ? t('exams.runner.continueExam') : t('exams.runner.continueFullscreen')}
            </Button>
          </div>
        </div>
      )}

      <div style={proctoring.warning || proctoring.needsFullscreen ? { filter: 'blur(14px)' } : undefined}>
      {/* Top bar: progress + countdown */}
      <div className={'mb-4 flex items-center justify-between ' + (reserveTopRight ? 'pr-9' : '')}>
        <div className="text-[13px] text-muted">
          {/* Named component, not positional numbering — see the M15 Decisions-log entry on why
              <Trans>'s <0>/<1> tag numbering is never used in this codebase. */}
          <Trans
            i18nKey="exams.runner.questionOf"
            values={{ current: current + 1, total: questions.length }}
            components={{ bold: <span className="font-semibold text-ink" /> }}
          />
          <span className="ml-2 text-muted">· {t('exams.runner.answeredCount', { count: answeredCount })}</span>
          {proctored && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-warn/15 px-2 py-0.5 text-[11px] font-medium text-warn">
              <Icon.lock className="h-3 w-3" /> {t('exams.runner.monitored')}
            </span>
          )}
        </div>
        {remaining !== null && (
          <div className={'font-mono text-[15px] font-semibold ' + (remaining < 60 ? 'text-danger' : 'text-ink')}>
            {mmss(remaining)}
          </div>
        )}
      </div>

      {/* Question navigator strip */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        {questions.map((q, i) => {
          const done = (answers[q.id]?.length ?? 0) > 0;
          const isCurrent = i === current;
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => setCurrent(i)}
              // The done/not-done split is carried by fill colour, which a screen reader can't see —
              // so the state goes in the label too.
              aria-label={
                done
                  ? t('exams.runner.goToQuestionAnswered', { number: i + 1 })
                  : t('exams.runner.goToQuestion', { number: i + 1 })
              }
              className={
                'h-7 w-7 rounded text-[12px] font-medium transition-colors ' +
                (isCurrent
                  ? 'bg-ink text-bg'
                  : done
                    ? // Answered reads as a filled navy chip: the previous accent/15 tint was so
                      // faint on cream that "answered" and "still to do" looked alike at a glance,
                      // which is the one thing this strip exists to tell apart.
                      'bg-accent text-accent-ink hover:bg-accent-2'
                    : 'bg-surface-2 text-muted hover:bg-surface-3')
              }
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      {/* Current question */}
      <div className="rounded-lg border border-line bg-surface p-5">
        {question && <QuestionBody question={question} selected={answers[question.id] ?? []} onToggle={toggle} />}
      </div>

      {/* Footer nav + submit */}
      <div className="mt-5 flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0}>
          <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('exams.runner.previous')}
        </Button>

        {current < questions.length - 1 ? (
          <Button variant="default" onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}>
            {t('exams.runner.next')} <Icon.chevronRight className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="accent" disabled={submit.isPending}>
                {submit.isPending ? <Spinner className="w-4 h-4" /> : t('exams.runner.submitExam')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('exams.runner.submitTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('exams.runner.submitBody', { count: answeredCount, total: questions.length })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel type="button">{t('exams.runner.keepGoing')}</AlertDialogCancel>
                <Button variant="accent" onClick={doSubmit} disabled={submit.isPending}>
                  {submit.isPending ? <Spinner className="w-4 h-4" /> : t('exams.runner.submit')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      </div>
    </div>
  );
}

// Route host (`/exam-attempts/:id/run`) — kept for deep links / resume from outside the landing.
// Frames the runner in the same centered column the rest of the app uses.
export function ExamRunnerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const onSubmitted = useCallback(() => {
    if (id) navigate(`/exam-attempts/${id}/results`, { replace: true });
  }, [id, navigate]);
  return (
    <div className="mx-auto max-w-2xl">
      <ExamRunner attemptId={id} onSubmitted={onSubmitted} />
    </div>
  );
}

// Focused-modal host — runs the test in a shadcn Dialog so only the test is on screen. Outside
// clicks are ignored (a stray click shouldn't yank the referee out mid-test); the ✕ / Esc still
// close it, and progress is autosaved so they can resume. `onSubmitted` fires once the attempt is
// finalised so the caller can route to results.
export function ExamRunnerDialog({
  attemptId,
  open,
  onOpenChange,
  onSubmitted,
}: {
  attemptId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="max-h-[92vh] w-[min(92vw,44rem)] max-w-none overflow-y-auto scroll-thin"
      >
        <DialogTitle className="sr-only">{t('exams.runner.examInProgress')}</DialogTitle>
        <ExamRunner attemptId={attemptId} onSubmitted={onSubmitted} reserveTopRight />
      </DialogContent>
    </Dialog>
  );
}
